import React, { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Flex,
  Collapse,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { keyframes } from '@emotion/react';
import ConfirmationModal from './ConfirmationModal';

// Modern animation for smooth entry
const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Gradient presets for different depth levels
const DEPTH_GRADIENTS = {
  0: {
    bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    hoverBg: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
    borderAccent: "linear-gradient(90deg, #667eea, #764ba2)",
  },
  1: {
    bg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    hoverBg: "linear-gradient(135deg, #e881f9 0%, #f3455a 100%)",
    borderAccent: "linear-gradient(90deg, #f093fb, #f5576c)",
  },
  2: {
    bg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    hoverBg: "linear-gradient(135deg, #3d9afc 0%, #00e0ec 100%)",
    borderAccent: "linear-gradient(90deg, #4facfe, #00f2fe)",
  },
};

const CategoryTree = ({ categories, expandedCategories, toggleCategory, onEdit, onDelete }) => {
  // State for confirmation modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Color mode values for consistent theming - all at top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  
  // Glass effect backgrounds for different depths
  const depth0Bg = useColorModeValue('rgba(102, 126, 234, 0.05)', 'rgba(102, 126, 234, 0.1)');
  const depth1Bg = useColorModeValue('rgba(240, 147, 251, 0.05)', 'rgba(240, 147, 251, 0.1)');
  const depth2Bg = useColorModeValue('rgba(79, 172, 254, 0.05)', 'rgba(79, 172, 254, 0.1)');

  // Additional color values for children container and hover effects
  const childrenContainerBg = useColorModeValue('rgba(255, 255, 255, 0.7)', 'rgba(45, 55, 72, 0.7)');
  const childrenContainerBorder = useColorModeValue('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.05)');
  const childHoverBg = useColorModeValue('rgba(0, 0, 0, 0.02)', 'rgba(255, 255, 255, 0.02)');
  const childBg = useColorModeValue('rgba(0, 0, 0, 0.01)', 'rgba(255, 255, 255, 0.01)');

  // Handle delete confirmation
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      onDelete(categoryToDelete.id);
      setCategoryToDelete(null);
      setIsConfirmModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setCategoryToDelete(null);
    setIsConfirmModalOpen(false);
  };

  // Recursive function to render the category tree with modern styling
  const renderCategoryTree = (categories, depth = 0) => {
    const gradients = DEPTH_GRADIENTS[Math.min(depth, 2)];
    
    // Select background based on depth
    const glassEffectBg = depth === 0 ? depth0Bg : depth === 1 ? depth1Bg : depth2Bg;
    
    return categories.map((category, idx) => (
      <Box
        key={`${category.id}-${category.name}`}
        position="relative"
        bg={depth === 0 ? cardBg : 'transparent'}
        borderRadius={depth === 0 ? "xl" : "lg"}
        border={depth === 0 ? "1px solid" : "none"}
        borderColor={depth === 0 ? borderColor : 'transparent'}
        p={depth === 0 ? 4 : 3}
        mb={depth === 0 ? 0 : 2}
        boxShadow={depth === 0 ? `0 4px 6px -1px ${shadowColor}` : 'none'}
        transition="all 0.2s ease"
        overflow="hidden"
        _hover={depth === 0 ? {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 25px -3px ${shadowColor}`,
        } : {
          bg: childHoverBg,
        }}
      >
        {/* Gradient accent bar - only for top level */}
        {depth === 0 && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg={gradients.borderAccent}
          />
        )}
        
        {/* Category content with modern glass effect */}
        <Box
          position="relative"
          bg={depth === 0 ? glassEffectBg : childBg}
          borderRadius="lg"
          p={depth === 0 ? 3 : 2}
          backdropFilter={depth === 0 ? "blur(10px)" : "none"}
        >
          <Flex
            align="center"
            justify="space-between"
            cursor="pointer"
            onClick={() => toggleCategory(category.id)}
          >
            <HStack spacing={3}>
              {/* Modern expand/collapse icon */}
              {category.children?.length > 0 && (
                <Box
                  p={2}
                  bg={gradients.bg}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
                  transition="all 0.2s ease"
                  _hover={{
                    bg: gradients.hoverBg,
                    transform: "scale(1.05)",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory(category.id);
                  }}
                >
                  <Icon
                    as={expandedCategories[category.id] ? ChevronDownIcon : ChevronRightIcon}
                    color="white"
                    boxSize={4}
                  />
                </Box>
              )}
              
              {/* Category name with modern typography */}
              <Text 
                fontWeight={depth === 0 ? 'bold' : 'semibold'}
                fontSize={depth === 0 ? 'lg' : 'md'}
                color={textColor}
                bgGradient={depth === 0 ? gradients.borderAccent : undefined}
                bgClip={depth === 0 ? "text" : undefined}
              >
                {category.name}
              </Text>
            </HStack>
            
            {/* Modern action buttons */}
            <HStack spacing={2}>
              <IconButton
                icon={<EditIcon />}
                size="sm"
                bg="linear-gradient(135deg, #F59E0B, #D97706)"
                color="white"
                borderRadius="lg"
                boxShadow="0 2px 4px rgba(245, 158, 11, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #E49307, #C76E05)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 8px rgba(245, 158, 11, 0.4)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(category);
                }}
                aria-label="Edit Category"
              />
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                bg="linear-gradient(135deg, #EF4444, #DC2626)"
                color="white"
                borderRadius="lg"
                boxShadow="0 2px 4px rgba(239, 68, 68, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #E33E3E, #C51E1E)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 8px rgba(239, 68, 68, 0.4)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(category);
                }}
                aria-label="Delete Category"
              />
            </HStack>
          </Flex>
        </Box>

        {/* Modern animated children rendering */}
        {category.children?.length > 0 && (
          <Collapse in={expandedCategories[category.id]} animateOpacity>
            <Box mt={3}
              overflowX="auto"
              minW="320px"
              w="max-content"
            >
              {/* Children container box */}
              <Box
                bg={childrenContainerBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={childrenContainerBorder}
                p={4}
                ml={6}
                position="relative"
                backdropFilter="blur(5px)"
              >
                {/* Modern connector line */}
                <Box
                  position="absolute"
                  left={-3}
                  top="50%"
                  transform="translateY(-50%)"
                  width="12px"
                  height="2px"
                  bg={gradients.borderAccent}
                  borderRadius="full"
                  opacity={0.6}
                />
                
                <VStack align="stretch" spacing={0}>
                  {category.children.map((child, childIdx) => (
                    <Box
                      key={child.id}
                      style={{
                        animation: `${fadeInSlide} 0.3s ease`,
                        animationDelay: `${childIdx * 0.05}s`,
                        animationFillMode: 'both',
                      }}
                    >
                      {renderCategoryTree([child], depth + 1)}
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Box>
          </Collapse>
        )}
      </Box>
    ));
  };

  // Render the modern category tree
  return (
    <>
      <Box overflowX="auto" w="100%">
        <VStack align="stretch" spacing={3} minW="600px" w="max-content">
          {renderCategoryTree(categories)}
        </VStack>
      </Box>
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Ви впевнені, що хочете видалити категорію "${categoryToDelete?.name}"? Це також видалить всі підкategorії.`}
      />
    </>
  );
};

export default CategoryTree;
